import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// In-browser avatar (£0/min). Pixel-aligned frames (neutral/mid/open/blink) + procedural life:
// voice-synced mouth, emphasis nods + brow lifts, eye glances, breathing, listening lean-in.
const IMG_W = 848, IMG_H = 1264;
const PHOTO_FRAMES = {
  neutral: "/adelaida/neutral.jpg", smile: "/adelaida/v_smile.jpg", mid: "/adelaida/v_mid.jpg", open: "/adelaida/v_open.jpg", blink: "/adelaida/v_blink.jpg",
  features: { eyes: [[345, 347], [512, 347]], brows: { cx: 428, cy: 297, rx: 172, ry: 21 } },
};

const eyeMask = (eyes) => eyes.map(([x, y]) =>
  `radial-gradient(ellipse 6% 2.2% at ${(x / IMG_W) * 100}% ${(y / IMG_H) * 100}%, #000 55%, transparent 100%)`).join(",");
const browMask = (b) => `radial-gradient(ellipse ${(b.rx / IMG_W) * 100}% ${(b.ry / IMG_H) * 100}% at ${(b.cx / IMG_W) * 100}% ${(b.cy / IMG_H) * 100}%, #000 45%, transparent 100%)`;

function useCoverBox(ref) {
  const [box, setBox] = useState({ s: 0, w: 0, h: 0, l: 0, t: 0 });
  useEffect(() => {
    const el = ref.current;
    const fit = () => {
      const W = el.clientWidth, H = el.clientHeight;
      // portrait screens: fill; wide desktop screens: fit her whole face (mouth stays visible)
      const s = W / H > 0.9 ? (H / IMG_H) * 1.08 : Math.max(W / IMG_W, H / IMG_H);
      const w = IMG_W * s, h = IMG_H * s;
      setBox({ s, w, h, l: (W - w) / 2, t: (H - h) * 0.18 });
    };
    fit();
    const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return box;
}

export default function AdelaidaPortrait({ amplitudeRef, speakingRef, frames = PHOTO_FRAMES, status = "idle" }) {
  const { neutral, smile, mid, open, blink, features } = frames;
  const [loaded, setLoaded] = useState(false);
  const [blinking, setBlinking] = useState(0);
  const blinkNowRef = useRef(null);
  const rootRef = useRef(null);
  const headRef = useRef(null);
  const midRef = useRef(null);
  const openRef = useRef(null);
  const smileRef = useRef(null);
  const eyesRef = useRef(null);
  const browsRef = useRef(null);
  const glowRef = useRef(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const box = useCoverBox(rootRef);
  const scaleRef = useRef(1);
  scaleRef.current = box.s || 1;

  useEffect(() => {
    setLoaded(false);
    let done = 0;
    const srcs = [neutral, mid, open, blink, smile].filter(Boolean);
    srcs.forEach((src) => {
      const img = new Image();
      img.onload = img.onerror = () => { done += 1; if (done >= srcs.length) setLoaded(true); };
      img.src = src;
    });
  }, [neutral, mid, open, blink, smile]);

  // natural blinking — single or occasional double blink every ~3–6s
  useEffect(() => {
    let timers = [];
    let cancelled = false;
    let next = null, last = 0;
    const blinkOnce = (cb) => {
      last = performance.now();
      setBlinking(Math.random() < 0.12 ? 0.55 : 1); // now and then a soft half-blink
      timers.push(setTimeout(() => { setBlinking(0); cb && cb(); }, 95 + Math.random() * 90));
    };
    const schedule = () => {
      if (cancelled) return;
      clearTimeout(next);
      next = setTimeout(() => {
        if (cancelled) return;
        if (Math.random() < 0.22) blinkOnce(() => timers.push(setTimeout(() => blinkOnce(schedule), 160 + Math.random() * 80)));
        else blinkOnce(schedule);
      }, 2200 + Math.random() * 3800);
      timers.push(next);
    };
    // people blink as they start speaking and at the end of a thought
    blinkNowRef.current = () => { if (performance.now() - last > 900) blinkOnce(schedule); };
    schedule();
    return () => { cancelled = true; timers.forEach(clearTimeout); blinkNowRef.current = null; };
  }, []);

  // one loop drives mouth, head, brows, eyes and light from her real voice
  useEffect(() => {
    let raf;
    const s = { smile: 0, quietSince: 0, v: 0, slow: 0, talk: 0, nod: 0, brow: 0, gx: 0, gy: 0, tx: 0, ty: 0, nextGlance: 0, lastHit: 0, lean: 0,
      wasSpeaking: false, inPause: false, settle: 0, tilt: 0, tiltT: 0, nextTilt: 0, jx: 0, jy: 0 };
    const t0 = performance.now();
    const tick = (now) => {
      const t = (now - t0) / 1000;
      const sc = scaleRef.current;
      const st = statusRef.current;
      const speaking = speakingRef?.current;
      const target = speaking ? Math.max(0, Math.min(1, amplitudeRef?.current ?? 0)) : 0;
      // jaw opens quickly and closes a touch slower, like a real mouth; snaps shut on true silence
      s.v += (target - s.v) * (target > s.v ? 0.5 : target < 0.03 ? 0.42 : 0.24);
      s.slow += (s.v - s.slow) * 0.04;
      s.talk += ((speaking ? 1 : 0) - s.talk) * 0.03;
      const a = s.v;

      // mouth crossfade closed -> parted -> open
      if (midRef.current) midRef.current.style.opacity = Math.max(0, Math.min(1, a * 2.1)).toFixed(3);
      if (openRef.current) openRef.current.style.opacity = Math.max(0, Math.min(1, (a - 0.26) / 0.74)).toFixed(3);

      // warm smile: while listening, and in pauses between her sentences
      if (a > 0.06) s.quietSince = now;
      const pause = speaking && now - s.quietSince > 380;
      if (speaking && !s.wasSpeaking) { blinkNowRef.current?.(); s.brow = Math.max(s.brow, 0.5); }
      if (pause && !s.inPause) { s.settle = 1; if (Math.random() < 0.6) blinkNowRef.current?.(); } // end of a phrase: small settle + blink
      s.wasSpeaking = !!speaking; s.inPause = pause;
      s.settle *= 0.95;
      const smileTarget = st === "listening" ? 0.9 : pause ? 0.7 : speaking ? 0 : st === "thinking" ? 0.35 : 0.55;
      s.smile += (smileTarget - s.smile) * (smileTarget > s.smile ? 0.035 : 0.16);
      if (smileRef.current) smileRef.current.style.opacity = s.smile.toFixed(3);

      // emphasis: syllable peaks well above the running level trigger a nod + brow lift
      if (speaking && a - s.slow > 0.22 && now - s.lastHit > 520) {
        s.lastHit = now;
        s.nod = Math.min(1.4, s.nod + 0.8 + Math.random() * 0.4);
        if (Math.random() < 0.55) s.brow = Math.min(1.3, s.brow + 0.9);
      }
      s.nod *= 0.92; s.brow *= 0.955;

      // head: speech sway + emphasis nod + breathing + listening lean-in
      s.lean += ((st === "listening" ? 1 : 0) - s.lean) * 0.025;
      const breathe = Math.sin(t * 1.55) * 0.5 + 0.5;
      // a gentle, held head tilt that changes every few seconds (people don't sway on a loop)
      if (now > s.nextTilt) { s.tiltT = (Math.random() - 0.5) * (speaking ? 1.6 : 1.1) + s.lean * 0.6; s.nextTilt = now + 2500 + Math.random() * 4500; }
      s.tilt += (s.tiltT - s.tilt) * 0.012;
      const hx = (Math.sin(t * 0.9) * 1.8 + Math.sin(t * 2.3) * 0.6 + Math.sin(t * 0.41) * 0.7) * s.talk + Math.sin(t * 0.37) * 1.2 * s.lean;
      const hy = s.nod * 3.2 - breathe * 1.6 + s.lean * 2 + s.settle * 1.4;
      const hr = (Math.sin(t * 0.7 + 1) * 0.7 + Math.sin(t * 1.9) * 0.2) * s.talk + s.tilt + s.nod * 0.25;
      const hs = 1 + breathe * 0.004 + s.lean * 0.018 + a * 0.004;
      if (headRef.current) headRef.current.style.transform = `translate3d(${hx.toFixed(2)}px, ${hy.toFixed(2)}px, 0) rotate(${hr.toFixed(3)}deg) scale(${hs.toFixed(4)})`;

      // eyes: quick saccades — mostly on you while speaking, wandering a touch when listening/thinking
      if (now > s.nextGlance) {
        const roam = st === "thinking" ? 1 : speaking ? 0.35 : 0.7;
        const r = Math.random();
        s.tx = r < 0.5 ? 0 : (Math.random() - 0.5) * 3.6 * roam;
        s.ty = st === "thinking" && Math.random() < 0.6 ? -1.6 : (Math.random() - 0.5) * 1.2 * roam;
        s.nextGlance = now + 700 + Math.random() * (speaking ? 2600 : 1800);
      }
      s.gx += (s.tx - s.gx) * 0.35; s.gy += (s.ty - s.gy) * 0.35;
      if (Math.random() < 0.04) { s.jx = (Math.random() - 0.5) * 0.35; s.jy = (Math.random() - 0.5) * 0.25; } // tiny fixational micro-saccades
      s.jx *= 0.9; s.jy *= 0.9;
      if (eyesRef.current) eyesRef.current.style.transform = `translate3d(${((s.gx + s.jx) * sc).toFixed(2)}px, ${((s.gy + s.jy) * sc).toFixed(2)}px, 0)`;
      if (browsRef.current) browsRef.current.style.transform = `translate3d(0, ${(-(s.brow * 4.2 + s.lean * 1.5) * sc).toFixed(2)}px, 0)`;
      if (glowRef.current) glowRef.current.style.opacity = (0.12 + a * 0.35 + s.lean * 0.1).toFixed(3);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [amplitudeRef, speakingRef]);

  const layer = "absolute inset-0 w-full h-full";
  const fade = { opacity: loaded ? 1 : 0, transition: "opacity 400ms ease" };

  return (
    <div ref={rootRef} className="absolute inset-0 overflow-hidden" data-testid="adelaida-portrait" data-status={status}>
      <motion.div className="absolute inset-0" style={{ transformOrigin: "50% 85%" }}
        animate={{ x: [0, 4, -3, 2, -3, 0], y: [0, -2, 2, -1, 1, 0], rotate: [0, 0.5, -0.4, 0.3, -0.25, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}>
        <div ref={headRef} className="absolute" style={{ left: box.l, top: box.t, width: box.w, height: box.h, transformOrigin: "50% 62%", willChange: "transform",
            ...(box.l > 2 ? { WebkitMaskImage: "linear-gradient(to right, transparent, #000 14%, #000 86%, transparent)", maskImage: "linear-gradient(to right, transparent, #000 14%, #000 86%, transparent)" } : {}) }}>
          <img key={neutral} src={neutral} alt="" aria-hidden draggable={false} className={layer} style={fade} />
          {smile && <img key={smile} ref={smileRef} src={smile} alt="" aria-hidden draggable={false} className={layer} style={{ opacity: 0, willChange: "opacity" }} data-testid="adelaida-smile" />}
          <img key={mid} ref={midRef} src={mid} alt="" aria-hidden draggable={false} className={layer} style={{ opacity: 0, willChange: "opacity" }} />
          <img key={open} ref={openRef} src={open} alt="" aria-hidden draggable={false} className={layer} style={{ opacity: 0, willChange: "opacity" }} />
          {features && loaded && (
            <>
              <img ref={eyesRef} src={neutral} alt="" aria-hidden draggable={false} className={layer} data-testid="adelaida-eyes"
                style={{ WebkitMaskImage: eyeMask(features.eyes), maskImage: eyeMask(features.eyes), willChange: "transform" }} />
              <img ref={browsRef} src={neutral} alt="" aria-hidden draggable={false} className={layer} data-testid="adelaida-brows"
                style={{ WebkitMaskImage: browMask(features.brows), maskImage: browMask(features.brows), willChange: "transform" }} />
            </>
          )}
          <img key={blink} src={blink} alt="" aria-hidden draggable={false} className={layer}
            style={{ opacity: loaded ? blinking : 0, transition: blinking ? "opacity 45ms ease-in" : "opacity 80ms ease-out", willChange: "opacity" }} />
          <div ref={glowRef} className="absolute inset-0 pointer-events-none mix-blend-soft-light"
            style={{ opacity: 0.12, background: "radial-gradient(45% 30% at 48% 32%, rgba(255,226,196,0.9), transparent 70%)" }} />
        </div>
      </motion.div>

      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(120% 80% at 50% 30%, transparent 40%, rgba(6,9,18,0.55) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
        style={{ background: "linear-gradient(to top, rgba(6,9,18,0.9), transparent)" }} />

      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">{neutral.includes("coach-m") ? "Richard is arriving…" : "Adelaida is arriving…"}</div>
      )}
    </div>
  );
}

const MALE_FRAMES = {
  neutral: "/coach-m/neutral.jpg", smile: "/coach-m/v_smile.jpg", mid: "/coach-m/v_mid.jpg", open: "/coach-m/v_open.jpg", blink: "/coach-m/v_blink.jpg",
  features: { eyes: [[350, 445], [506, 445]], brows: { cx: 428, cy: 398, rx: 170, ry: 20 } },
};
const MALE_VOICES = ["onyx", "echo", "ash", "fable", "male"];
const framesForVoice = (voice) => (MALE_VOICES.includes(voice) ? MALE_FRAMES : PHOTO_FRAMES);

export { PHOTO_FRAMES, MALE_FRAMES, framesForVoice };
